import { Utils } from '@Util/utils'
import { MultiplierAnimation, MultiplierAnimationResult } from '../../multiplier-animation'
import { MI_MULTIPLIER_PUTTING_ASSETS, MI_MULTIPLIER_PUTTING_TEXTURE_KEYS } from './asset-preloader'
import { tweenPromise, tweenWaitPromise } from '@Animation/utils'
import { store } from '@AppState/redux-store'
import { AnimationActions } from '@AppState/animation'
import {
  MI_MULTIPLIER_SHARED_ATLAS_FRAMES,
  MI_MULTIPLIER_SHARED_TEXTURE_KEYS,
} from '../shared/constants'

type HoleData = {
  x: number
  y: number
  flagValue: number
}

const HOLE_POSITIONS: HoleData[] = [
  {
    x: -250,
    y: -150,
    flagValue: 1,
  },
  {
    x: 0,
    y: -200,
    flagValue: 2,
  },
  {
    x: 250,
    y: -150,
    flagValue: 3,
  },
  {
    x: -100,
    y: -50,
    flagValue: 4,
  },
  {
    x: 100,
    y: -50,
    flagValue: 5,
  },
]

type BounceData = {
  x: number
  y: number
  index: number
  initialBounceTarget: boolean
  ballScale: number
  validNextBounceIndices: number[]
  validHoles: number[]
}

const BOUNCE_DATA: BounceData[] = [
  {
    x: -100,
    y: -250,
    index: 0,
    initialBounceTarget: true,
    ballScale: 0.25,
    validNextBounceIndices: [2, 4, 5],
    validHoles: [0, 1, 2, 4],
  },
  {
    x: 100,
    y: -250,
    index: 1,
    initialBounceTarget: true,
    ballScale: 0.25,
    validNextBounceIndices: [2, 3, 5],
    validHoles: [0, 1, 2, 3],
  },
  {
    x: 500,
    y: -125,
    index: 2,
    initialBounceTarget: false,
    ballScale: 0.3,
    validNextBounceIndices: [0, 1, 3],
    validHoles: [2, 3, 4],
  },
  {
    x: 400,
    y: 25,
    index: 3,
    initialBounceTarget: false,
    ballScale: 0.4,
    validNextBounceIndices: [1, 2, 4],
    validHoles: [1, 2, 3, 4],
  },
  {
    x: -400,
    y: 25,
    index: 4,
    initialBounceTarget: false,
    ballScale: 0.4,
    validNextBounceIndices: [0, 3, 5],
    validHoles: [0, 1, 3, 4],
  },
  {
    x: -500,
    y: -125,
    index: 5,
    initialBounceTarget: false,
    ballScale: 0.3,
    validNextBounceIndices: [0, 1, 4],
    validHoles: [0, 3, 4],
  },
]

const MAX_BOUNCES = 4

export class MichiganPuttingMultiplierAnimation extends MultiplierAnimation {
  earlyBackground1!: Phaser.GameObjects.Image
  puttingGreen!: Phaser.GameObjects.Image
  ball!: Phaser.GameObjects.Sprite
  windMill!: Phaser.GameObjects.Image
  putter: Phaser.GameObjects.Image
  flags: Phaser.GameObjects.Image[] = []
  flagPoles: Phaser.GameObjects.Image[] = []
  shadow: Phaser.GameObjects.Image

  holeMaskObj: Phaser.GameObjects.Image
  holeMask: Phaser.Display.Masks.BitmapMask

  bladeContainer: Phaser.GameObjects.Container

  phase1Layer1Container: Phaser.GameObjects.Container
  phase1Layer2Container: Phaser.GameObjects.Container

  constructor(scene: Phaser.Scene, x: number, y: number, config: MultiplierAnimationResult) {
    super(scene, x, y, config)

    if (!scene.anims.exists(MI_MULTIPLIER_PUTTING_ASSETS.BALL_ANIMATION)) {
      scene.anims.create({
        key: MI_MULTIPLIER_PUTTING_ASSETS.BALL_ANIMATION,
        frames: scene.anims.generateFrameNumbers(MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.GOLF_BALL, {
          start: 0,
          end: 8,
        }),
        duration: 450,
        repeat: -1,
        hideOnComplete: false,
      })
    }

    this.ball = this.scene.add.sprite(0, 200, MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.GOLF_BALL, 0)
    this.ball.setOrigin(0.5, 0.5)
    this.ball.setScale(0.6)

    this.earlyBackground1 = this.scene.add.image(
      -5,
      200,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.BACKGROUND1,
    )
    this.earlyBackground1.setOrigin(0.5, 1.0)
    this.earlyBackground1.setScale(1)

    this.puttingGreen = this.scene.add.image(
      0,
      600,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.BACKGROUND2,
    )
    this.puttingGreen.setOrigin(0.5, 1.0)

    this.windMill = this.scene.add.image(
      0,
      -225,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.WINDMILL,
    )
    this.windMill.setOrigin(0.5, 0.5)

    this.putter = this.scene.add.image(
      -75,
      250,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.PUTTER,
    )
    this.putter.setAlpha(0)
    this.putter.setOrigin(0.5, 1.0)

    this.bladeContainer = this.scene.add.container(0, -260)
    for (let i = 0; i < 4; i++) {
      const blade = this.scene.add.image(
        0,
        0,
        MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
        MI_MULTIPLIER_PUTTING_ASSETS.BLADE,
      )
      blade.setOrigin(0.5, 0)
      blade.setRotation(Utils.degreesToRadians(90 * i))
      this.bladeContainer.add(blade)
    }

    this.scene.tweens.add({
      targets: this.bladeContainer,
      rotation: Math.PI * 2,
      duration: 4300,
      repeat: -1,
    })

    this.shadow = this.scene.add.image(
      0,
      400,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.DOORWAY,
    )
    this.shadow.setScale(6)
    this.shadow.alpha = 0

    this.holeMaskObj = this.scene.add.image(
      0,
      0,
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.HOLE_MASK,
    )
    this.holeMaskObj.setVisible(false)
    this.holeMask = new Phaser.Display.Masks.BitmapMask(this.scene, this.holeMaskObj, 0, 0)

    this.phase1Layer1Container = this.scene.add.container(0, 0, [
      this.earlyBackground1,
      this.puttingGreen,
      this.windMill,
      this.bladeContainer,
    ])

    this.phase1Layer2Container = this.scene.add.container(0, 0, [
      this.windMill,
      this.bladeContainer,
    ])

    this.add([
      this.phase1Layer1Container,
      this.phase1Layer2Container,
      this.ball,
      this.putter,
      this.shadow,
    ])
  }

  async play() {
    await this.showKickerLogo()
    await this.putBallIntoWindmill()
    await this.ballComesOut()
    await this.ballToHole()
    await this.showResult()

    await tweenWaitPromise(this.scene, 1500)()

    await this.cleanup()

    this.destroy()
    return
  }

  async showKickerLogo() {
    const { width, height } = this.scene.sys.game.canvas

    const overlay = this.scene.add.graphics()
    overlay.fillStyle(0x000000, 0.5)
    overlay.fillRect(-width / 2, -height / 2, width, height)
    overlay.setAlpha(0)

    const heres = this.scene.add.image(
      -500,
      -100,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.HERES,
    )
    heres.setOrigin(0.5, 0.5)

    const the = this.scene.add.image(
      500,
      -25,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.THE,
    )
    the.setOrigin(0.5, 0.5)

    const kicker = this.scene.add.image(
      0,
      50,
      MI_MULTIPLIER_SHARED_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_SHARED_ATLAS_FRAMES.KICKER,
    )
    kicker.setOrigin(0.5, 0.5)

    kicker.alpha = 0

    const introDuration = 300

    const introOverlay = tweenPromise(this.scene, {
      targets: overlay,
      alpha: 1,
      duration: introDuration,
    })

    const introHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 10,
      duration: introDuration,
    })
    const introThe = tweenPromise(this.scene, {
      targets: the,
      x: -10,
      duration: introDuration,
    })
    const introKicker = tweenPromise(this.scene, {
      targets: kicker,
      alpha: 1,
      duration: introDuration,
    })

    this.add([overlay, kicker, heres, the])

    await Promise.all([introOverlay(), introHeres(), introThe(), introKicker()])

    const driftDuration = 1100
    const driftHeres = tweenPromise(this.scene, {
      targets: heres,
      x: -25,
      duration: driftDuration,
    })

    const driftThe = tweenPromise(this.scene, {
      targets: the,
      x: 25,
      duration: driftDuration,
    })

    await Promise.all([driftHeres(), driftThe()])

    const outroDuration = 300

    const outroHeres = tweenPromise(this.scene, {
      targets: heres,
      x: 500,
      duration: outroDuration,
    })
    const outroThe = tweenPromise(this.scene, {
      targets: the,
      x: -500,
      duration: outroDuration,
    })
    const outroKicker = tweenPromise(this.scene, {
      targets: kicker,
      scale: 0.4,
      // y: 200,
      alpha: 0,
      duration: outroDuration,
    })

    const outroOverlay = tweenPromise(this.scene, {
      targets: overlay,
      alpha: 0,
      duration: outroDuration,
    })

    this.showPutter()
    await Promise.all([outroHeres(), outroThe(), outroKicker(), outroOverlay()])

    heres.destroy()
    the.destroy()
    kicker.destroy()
  }

  async showPutter() {
    await tweenPromise(this.scene, {
      targets: this.putter,
      alpha: 1,
      x: -25,
      scale: 0.7,
      duration: 400,
    })()
  }

  async swingClub() {
    await tweenPromise(this.scene, {
      targets: this.putter,
      delay: 500,
      y: this.putter.y + 50,
      scale: 0.9,
      duration: 300,
      yoyo: true,
    })()
  }

  async putterFollowThrough() {
    await tweenPromise(this.scene, {
      targets: this.putter,
      y: -100,
      x: -50,
      scaleY: 0.36,
      scaleX: 0.4,
      duration: 650,
      ease: Phaser.Math.Easing.Cubic.Out,
    })()

    await tweenPromise(this.scene, {
      targets: this.putter,
      alpha: 0,
      y: -475,
      x: -25,
      duration: 650,
    })()
  }

  async putBallIntoWindmill() {
    await this.swingClub()

    const delay = 100
    const phase1Duration = 1000
    const phase2Duration = 1250

    const ballMovement = tweenPromise(this.scene, {
      targets: this.ball,
      y: 0,
      scale: this.ball.scale * 0.9,
      duration: phase1Duration / 2,
    })

    const ballScaling = tweenPromise(this.scene, {
      targets: this.ball,
      delay: delay,
      scale: this.ball.scale * 0.9,
      duration: phase1Duration,
    })

    const backgroundScaling = tweenPromise(this.scene, {
      targets: [this.phase1Layer1Container, this.phase1Layer2Container],
      delay: delay,
      duration: phase1Duration + phase2Duration,
      ease: Phaser.Math.Easing.Sine.InOut,
      scale: 28,
      y: 3500,
    })

    this.ball.play(MI_MULTIPLIER_PUTTING_ASSETS.BALL_ANIMATION)

    this.putterFollowThrough()
    backgroundScaling()

    await Promise.all([ballScaling(), ballMovement()])

    this.bringToTop(this.phase1Layer2Container)

    const fullZoomBall = tweenPromise(this.scene, {
      targets: [this.ball],
      delay: phase2Duration / 3,
      duration: (2 * phase2Duration) / 3,
      scale: 0.75,
    })

    const fadeInShadow = tweenPromise(this.scene, {
      targets: this.shadow,
      delay: phase2Duration / 3,
      duration: (2 * phase2Duration) / 3,
      alpha: 0.5,
    })

    const hideWindmillBlades = tweenPromise(this.scene, {
      targets: [this.bladeContainer],
      delay: phase2Duration - 100,
      duration: 100,
      alpha: 0,
    })

    await Promise.all([fullZoomBall(), fadeInShadow(), hideWindmillBlades()])

    this.setupFinalGreen()

    this.bladeContainer.setVisible(false)
  }
  getDurationBetweenPoints(
    pointA: Phaser.Types.Math.Vector2Like,
    pointB: Phaser.Types.Math.Vector2Like,
    velocity: number,
  ) {
    const distance = Math.abs(
      Math.sqrt(Math.pow(pointA.x - pointB.x, 2) + Math.pow(pointA.y - pointB.y, 2)),
    )
    return distance / velocity
  }

  async ballComesOut() {
    tweenPromise(this.scene, {
      targets: this.shadow,
      duration: 250,
      alpha: 0,
    })()

    const ballScale = tweenPromise(this.scene, {
      targets: this.ball,
      scale: 0.25,
      duration: 500,
    })

    const zoomOut = tweenPromise(this.scene, {
      targets: [this.phase1Layer1Container, this.phase1Layer2Container],
      scale: 0.5,
      duration: 500,
      ease: Phaser.Math.Easing.Quadratic.InOut,
    })

    await Promise.all([ballScale(), zoomOut()])
  }

  setupFinalGreen() {
    this.windMill.setVisible(false)
    this.puttingGreen.setTexture(
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      MI_MULTIPLIER_PUTTING_ASSETS.GREEN_END,
    )
    this.puttingGreen.setScale(2)
    this.earlyBackground1.setScale(2)
    this.earlyBackground1.y = 650
    this.phase1Layer1Container.y = 0
    this.phase1Layer2Container.y = 0

    this.createHoles()
  }

  createHoles() {
    const possibleValues = Utils.generateRandomNumberArray(5, 1, 5)

    for (let i = 0; i < 5; i++) {
      const flagValue = possibleValues[i]
      HOLE_POSITIONS[i].flagValue = flagValue

      const hole = this.scene.add.image(
        HOLE_POSITIONS[i].x,
        HOLE_POSITIONS[i].y,
        MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
        MI_MULTIPLIER_PUTTING_ASSETS.HOLE,
      )
      hole.setScale(0.75)

      const pole = this.scene.add.image(
        HOLE_POSITIONS[i].x,
        HOLE_POSITIONS[i].y - 25,
        MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
        MI_MULTIPLIER_PUTTING_ASSETS.FLAGPOLE,
      )

      const flag = this.scene.add.image(
        HOLE_POSITIONS[i].x - 10,
        HOLE_POSITIONS[i].y - 110,
        MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
        `${MI_MULTIPLIER_PUTTING_ASSETS.FLAG_PREFIX}${flagValue}.png`,
      )
      flag.setOrigin(0, 0.5)
      flag.setScale(1)

      this.flags.push(flag)
      this.flagPoles.push(pole)

      this.phase1Layer1Container.add(hole)
      this.phase1Layer2Container.add([pole, flag])
    }
  }

  async ballToHole() {
    const targetIndex = this.multiplierValue - 1
    const targetHole = HOLE_POSITIONS.find((val) => val.flagValue === this.multiplierValue)

    if (!targetHole) {
      store.dispatch(AnimationActions.crash(`Putting Multiplier | Unable to determine target hole`))
      return
    }

    this.holeMaskObj.setPosition(
      this.x + this.phase1Layer1Container.scale * targetHole.x,
      this.y + this.phase1Layer1Container.scale * targetHole.y,
    )

    this.holeMaskObj.setScale(0.85 * this.phase1Layer1Container.scale)
    this.holeMask = new Phaser.Display.Masks.BitmapMask(this.scene, this.holeMaskObj, 0, 0)

    const targetEllipse = new Phaser.Geom.Ellipse(targetHole.x, targetHole.y, 150, 50)

    const path: BounceData[] = []

    for (let bounces = 0; bounces < MAX_BOUNCES; bounces++) {
      let validNextBounce = []
      if (bounces === 0) {
        validNextBounce = BOUNCE_DATA.filter((val) =>
          val.validNextBounceIndices.includes(targetIndex),
        )
      } else if (bounces === MAX_BOUNCES - 1) {
        validNextBounce = BOUNCE_DATA.filter(
          (val) =>
            val.validNextBounceIndices.includes(path[bounces - 1].index) && val.initialBounceTarget,
        )
      } else {
        validNextBounce = BOUNCE_DATA.filter((val) =>
          val.validNextBounceIndices.includes(path[bounces - 1].index),
        )
      }

      if (validNextBounce.length === 0) {
        store.dispatch(AnimationActions.crash(`Putting Multiplier | Cannot find proper ball path`))
        return
      }

      const bounceTarget = Utils.getRandomElementFromArray(validNextBounce)
      path.push(bounceTarget)

      if (bounceTarget.initialBounceTarget && bounces > 0) {
        break
      }
    }

    let finalTargetIndex =
      Phaser.Math.Angle.BetweenPoints(path[0], targetEllipse) / Phaser.Math.PI2 - 0.5
    if (finalTargetIndex < 0) finalTargetIndex += 1

    const finalTarget = targetEllipse.getPoint(finalTargetIndex)

    this.ball.setPosition(
      this.ball.x + this.phase1Layer1Container.x,
      this.ball.y + this.phase1Layer2Container.y,
    )

    this.ball.setScale(this.ball.scale / this.phase1Layer1Container.scale)

    this.phase1Layer1Container.add(this.ball)

    let ballVelocity = 0.7

    for (let i = path.length - 1; i >= 0; i--) {
      await tweenPromise(this.scene, {
        targets: this.ball,
        x: path[i].x,
        y: path[i].y,
        scale: path[i].ballScale,
        duration: this.getDurationBetweenPoints(
          { x: this.ball.x, y: this.ball.y },
          path[i],
          ballVelocity,
        ),
      })()
      ballVelocity -= 0.02
    }

    await tweenPromise(this.scene, {
      targets: this.ball,
      x: finalTarget.x,
      y: finalTarget.y,
      duration: this.getDurationBetweenPoints(
        { x: this.ball.x, y: this.ball.y },
        finalTarget,
        ballVelocity,
      ),
    })()

    this.ball.setMask(this.holeMask)

    const duration = this.getDurationBetweenPoints(this.ball, targetHole, ballVelocity)

    const fallX = tweenPromise(this.scene, {
      targets: this.ball,
      x: targetHole.x,
      duration: duration,
    })

    const fallY = tweenPromise(this.scene, {
      targets: this.ball,
      y: targetHole.y + 60,
      duration: duration,
      scale: this.ball.scale * 0.75,
      ease: Phaser.Math.Easing.Circular.In,
    })

    await Promise.all([fallX(), fallY()])

    this.ball.setVisible(false)

    await Utils.sleep(200)
  }

  async showResult() {
    const index = HOLE_POSITIONS.findIndex((val) => val.flagValue === this.multiplierValue)

    const winningFlag = this.flags.splice(index, 1)[0]

    const hideLosingFlags = tweenPromise(this.scene, {
      targets: [...this.flags, ...this.flagPoles],
      alpha: 0,
      duration: 250,
    })

    winningFlag.setTexture(
      MI_MULTIPLIER_PUTTING_TEXTURE_KEYS.ATLAS,
      `${MI_MULTIPLIER_PUTTING_ASSETS.FLAG_WIN_PREFIX}${this.multiplierValue}.png`,
    )
    winningFlag.setScale(0.33)

    const displayWinningFlag = tweenPromise(this.scene, {
      targets: winningFlag,
      x: -(1.25 * winningFlag.width) / 2,
      y: 0,
      scale: 1.25,
      duration: 400,
    })

    await Promise.all([hideLosingFlags(), displayWinningFlag()])
    await Utils.sleep(1000)
  }

  async cleanup() {
    await tweenPromise(this.scene, {
      targets: this,
      alpha: 0,
      duration: 1000,
    })()
  }

  destroy(fromScene?: boolean): void {
    this.holeMaskObj.destroy()
    this.phase1Layer1Container.removeAll(true)
    this.phase1Layer2Container.removeAll(true)

    super.destroy(fromScene)
  }
}
